// ============================================================================
//  tokens.js — the Mile Marker design system.
//
//  Everything visual that more than one screen needs lives here: brand colors,
//  the 5F definitions, RAG status tokens, the injected <style> string, and the
//  shared inline-style objects.
//
//  These used to be module-scoped consts inside MileMarker.jsx, which meant
//  every new screen had to re-declare its own copy of the palette. Import from
//  here instead — never re-declare a hex value in a component file.
// ============================================================================

import { BookOpen, Shield, Users, TrendingUp, Target } from "lucide-react";

/* ------------------------------------------------------------------ */
/*  BRAND — Men of Iron Brand Standards Rev 08.2016                    */
/*  Red  PMS 199  → #D50032   |  Dark PMS 433 → #132229                */
/*  Primary staging is the logo on WHITE (default version).            */
/* ------------------------------------------------------------------ */

export const C = {
  red: "#D50032",
  redDk: "#B0002A",
  redSoft: "rgba(213,0,50,0.10)",
  redLine: "rgba(213,0,50,0.35)",
  ink: "#132229",          // PMS 433 — primary text
  bg: "#FFFFFF",
  shell: "#F4F5F4",         // app backdrop, faint cool grey
  panel: "#FFFFFF",
  line: "rgba(19,34,41,0.12)",
  lineHi: "rgba(19,34,41,0.22)",
  text: "#132229",
  sub: "#5B6A70",
  faint: "#93A0A4",
};

/* ---- the five F's -------------------------------------------------- */

export const FS = [
  { key: "faith",    label: "Faith",    blurb: "The foundation. It starts with God.", icon: BookOpen },
  { key: "family",   label: "Family",   blurb: "Lead at home before anywhere else.",  icon: Shield },
  { key: "friends",  label: "Friends",  blurb: "Don't do life alone.",                icon: Users },
  { key: "fitness",  label: "Fitness",  blurb: "Steward the body you were given.",    icon: TrendingUp },
  { key: "finances", label: "Finances", blurb: "Manage what you've been entrusted.",  icon: Target },
];

/* ---- red / amber / green ------------------------------------------- */

export const RAG = {
  green: { label: "On Track",  dot: "#2E8B57", soft: "rgba(46,139,87,0.12)",  text: "#1F6B40" },
  amber: { label: "At Risk",   dot: "#C8881C", soft: "rgba(200,136,28,0.14)", text: "#9A6710" },
  red:   { label: "Off Track", dot: C.red,     soft: C.redSoft,               text: C.redDk },
};

/* ------------------------------------------------------------------ */
/*  GLOBAL STYLE                                                       */
/*                                                                     */
/*  Injected once as <style>{fonts}</style>. Hover states live here as  */
/*  real CSS rather than onMouseEnter handlers mutating                 */
/*  e.currentTarget.style — those did not survive a re-render.          */
/* ------------------------------------------------------------------ */

export const fonts = `
@import url('https://fonts.googleapis.com/css2?family=Oswald:wght@500;600;700&family=Barlow:wght@400;500;600;700&display=swap');
*{ -webkit-font-smoothing:antialiased; box-sizing:border-box; }
input[type=range]{ height:4px; }
::placeholder{ color:${C.faint}; }

.mm-card{
  transition: transform 160ms, box-shadow 160ms, border-color 160ms;
}
.mm-card:hover{
  transform: translateY(-3px);
  box-shadow: 0 10px 26px rgba(19,34,41,0.10);
  border-color: ${C.redLine};
}
.mm-menu-item{ transition: background 120ms; }
.mm-menu-item:hover{ background:${C.shell}; }

@keyframes coach-nudge-in{
  from{ opacity:0; transform:translateY(-4px); }
  to{   opacity:1; transform:none; }
}
@keyframes mm-pulse{
  0%,100%{ opacity:.55; }
  50%{     opacity:1; }
}

/* The header packs a wordmark, the tab group and the action buttons onto one
   row. On a phone that overflows, so let it wrap and tighten the tabs. */
.mm-header{ display:flex; align-items:center; justify-content:space-between;
  gap:10px; flex-wrap:wrap; }
@media (max-width: 560px){
  .mm-header{ justify-content:center; }
  .mm-tab{ padding:7px 12px !important; font-size:12.5px !important; }
}
@media (max-width: 380px){
  .mm-tab{ padding:6px 9px !important; font-size:12px !important; }
}
`;

/* ------------------------------------------------------------------ */
/*  SHARED INLINE STYLES                                               */
/* ------------------------------------------------------------------ */

export const display = { fontFamily: "'Oswald', sans-serif", fontWeight: 600, letterSpacing: ".005em" };
export const wordmark = { fontFamily: "'Oswald', sans-serif", fontWeight: 700, letterSpacing: ".10em" };

export const page = {
  minHeight: "100vh",
  background: C.shell,
  fontFamily: "'Barlow', system-ui, sans-serif",
  color: C.text,
};

export const kicker = {
  fontFamily: "'Oswald', sans-serif", fontSize: 11, letterSpacing: ".20em",
  textTransform: "uppercase", color: C.red, fontWeight: 600,
};

export const overlay = {
  position: "fixed", inset: 0, background: "rgba(19,34,41,0.45)", backdropFilter: "blur(3px)",
  display: "grid", placeItems: "center", zIndex: 100, padding: 20, overflowY: "auto",
};
export const sheet = {
  background: C.bg, borderRadius: 18, padding: "26px 28px", width: "100%",
  boxShadow: "0 30px 70px rgba(19,34,41,0.28)", border: `1px solid ${C.line}`,
  maxHeight: "90vh", overflowY: "auto",
};
export const lbl = { fontSize: 12.5, fontWeight: 600, color: C.sub, letterSpacing: ".01em" };
export const inp = {
  width: "100%", marginTop: 7, padding: "10px 13px", borderRadius: 9,
  border: `1.5px solid ${C.line}`, background: "#FBFBFB",
  fontFamily: "'Barlow', sans-serif", fontSize: 14, color: C.text, outline: "none",
};
export const primaryBtn = {
  display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 18px",
  borderRadius: 9, border: "none", cursor: "pointer", background: C.red,
  color: "#fff", fontSize: 13, fontWeight: 700, fontFamily: "'Barlow', sans-serif",
  letterSpacing: ".02em",
};
export const smallBtn = {
  display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 13px",
  borderRadius: 8, border: `1px solid ${C.redLine}`, cursor: "pointer",
  background: C.redSoft, color: C.redDk, fontSize: 12.5, fontWeight: 700,
  fontFamily: "'Barlow', sans-serif",
};
export const smallGhost = {
  display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 12px",
  borderRadius: 8, border: "none", cursor: "pointer", background: "transparent",
  color: C.sub, fontSize: 12.5, fontWeight: 600, fontFamily: "'Barlow', sans-serif",
};
export const iconBtn = {
  width: 32, height: 32, borderRadius: 8, border: "none", cursor: "pointer",
  background: C.shell, color: C.sub, display: "grid", placeItems: "center",
};
export const iconBtnSm = {
  width: 28, height: 28, borderRadius: 7, border: "none", cursor: "pointer",
  background: "transparent", color: C.faint, display: "grid", placeItems: "center",
};
